import { useState, useEffect, useRef } from 'react';

interface ArticlesGridProps {
  selectedCategory: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  searchQuery: string;
}

interface Article {
  id: number;
  title: string;
  category: string[];
  date: string;
  excerpt: string;
  image: string;
  readTime: string;
  author: {
    name: string;
    image: string;
  };
}

export default function ArticlesGrid({ 
  selectedCategory, 
  currentPage, 
  onPageChange, 
  searchQuery 
}: ArticlesGridProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const allArticles: Article[] = [
    {
      id: 1,
      title: "The Art of Prompt Engineering: Crafting AI's Best Output for Marketing",
      category: ["AI Strategy & Implementation", "Practical Guides & Tutorials"],
      date: "June 3, 2025",
      excerpt: "Unlock the true potential of generative AI. This comprehensive guide delves into advanced prompt engineering techniques that elevate your marketing content from good to exceptional. Learn the psychology behind effective prompts and master the art of AI communication.",
      image: "/assets/prompt-eng-1.jpg",
      readTime: "12 min read",
      author: {
        name: "Hash Adz AI Team",
        image: "/assets/author-ai-1.jpg"
      }
    },
    {
      id: 2,
      title: "How AI-Driven Data Analytics Predicts Consumer Behavior in the Kerala Market",
      category: ["Advanced Growth Marketing", "AI Strategy & Implementation"],
      date: "May 29, 2025",
      excerpt: "Explore how localized AI models are providing unprecedented foresight into consumer trends specific to Kerala, enabling hyper-targeted marketing campaigns and product development. Discover the power of regional data intelligence.",
      image: "/assets/kerala-analytics-2.jpg",
      readTime: "9 min read",
      author: {
        name: "Marketing Analytics Team",
        image: "/assets/author-marketing-2.jpg"
      }
    },
    {
      id: 3,
      title: "Building Your Brand in the Metaverse: A Step-by-Step Guide for Enterprises",
      category: ["Web3 & Future Tech Explored", "Brand Evolution & Design Theory"],
      date: "April 15, 2025",
      excerpt: "The metaverse isn't just a buzzword; it's a new frontier for brand engagement. This article walks you through the strategic considerations for establishing a compelling presence in virtual worlds, from avatar design to virtual real estate.",
      image: "/assets/metaverse-brand-3.jpg",
      readTime: "15 min read",
      author: {
        name: "Web3 Innovation Team",
        image: "/assets/author-web3-3.jpg"
      }
    },
    {
      id: 4,
      title: "Hyper-Personalization at Scale: The Future of Customer Experience",
      category: ["Advanced Growth Marketing", "AI Strategy & Implementation"],
      date: "May 15, 2025",
      excerpt: "Discover how AI-powered hyper-personalization is revolutionizing customer experiences. Learn to implement dynamic content, predictive recommendations, and real-time personalization strategies that convert browsers into loyal customers.",
      image: "/assets/personalization-4.jpg",
      readTime: "11 min read",
      author: {
        name: "Customer Experience Team",
        image: "/assets/author-cx-4.jpg"
      }
    },
    {
      id: 5,
      title: "The Psychology of Color in Digital Brand Identity",
      category: ["Brand Evolution & Design Theory"],
      date: "April 28, 2025",
      excerpt: "Dive deep into the psychological impact of color choices in digital branding. Understand how different cultures perceive colors, the neuroscience behind color preferences, and how to create emotionally resonant brand palettes.",
      image: "/assets/color-psychology-5.jpg",
      readTime: "8 min read",
      author: {
        name: "Brand Design Team",
        image: "/assets/author-brand-5.jpg"
      }
    },
    {
      id: 6,
      title: "Implementing AI Chatbots: From Strategy to Deployment",
      category: ["Practical Guides & Tutorials", "AI Strategy & Implementation"],
      date: "May 8, 2025",
      excerpt: "A comprehensive step-by-step guide to implementing AI chatbots that actually help your business. From choosing the right platform to training conversational AI, learn how to create chatbots that enhance customer experience.",
      image: "/assets/chatbot-guide-6.jpg",
      readTime: "14 min read",
      author: {
        name: "AI Implementation Team",
        image: "/assets/author-ai-impl-6.jpg"
      }
    }
  ];

  const filteredArticles = allArticles.filter(article => {
    const matchesCategory = selectedCategory === 'All Articles' || 
                           article.category.includes(selectedCategory);
    const matchesSearch = searchQuery === '' || 
                         article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.category.some(cat => cat.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const articlesPerPage = 6;
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const currentArticles = filteredArticles.slice(startIndex, startIndex + articlesPerPage);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    onPageChange(1);
  }, [selectedCategory, searchQuery]);

  return (
    <section 
      id="featured-articles"
      ref={sectionRef}
      className="py-20 bg-white relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Featured Articles & Latest Deep Dives
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            {filteredArticles.length} articles found
            {selectedCategory !== 'All Articles' && ` in ${selectedCategory}`}
            {searchQuery && ` for "${searchQuery}"`}
          </p>
        </div>

        {/* Articles Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {currentArticles.map((article, index) => (
            <article
              key={article.id}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 overflow-hidden border border-gray-100 cursor-pointer"
              onMouseEnter={() => setHoveredCard(article.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Article Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Category Tags */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {article.category.map((cat, catIndex) => (
                    <span
                      key={catIndex}
                      className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs px-3 py-1 rounded-full font-medium"
                    >
                      {cat}
                    </span>
                  ))}
                </div>

                {/* Read Time */}
                <div className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {article.readTime}
                </div>
              </div>

              {/* Article Content */}
              <div className="p-6">
                {/* Author and Date */}
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={article.author.image}
                    alt={article.author.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{article.author.name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-calendar-line"></i>
                      <span>{article.date}</span>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors duration-300 line-clamp-2">
                  {article.title}
                </h3>

                <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                  {article.excerpt}
                </p>

                <button className="group/btn inline-flex items-center gap-2 text-orange-600 font-semibold hover:text-red-600 transition-colors duration-300 whitespace-nowrap cursor-pointer">
                  Read Full Article
                  <i className="ri-arrow-right-line group-hover/btn:translate-x-1 transition-transform duration-300"></i>
                </button>
              </div>

              {/* Hover Effect Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-r from-orange-500/5 to-red-500/5 opacity-0 transition-opacity duration-300 ${
                hoveredCard === article.id ? 'opacity-100' : ''
              }`} />
            </article>
          ))}
        </div>

        {/* No Results Message */}
        {filteredArticles.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-2xl font-bold text-gray-600 mb-2">No articles found</h3>
            <p className="text-gray-500">Try adjusting your search or category filter</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`flex justify-center items-center gap-2 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer ${
                    currentPage === page
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}